import React, { useMemo } from "react";
import { MailCheck, Mail, ArrowRight, RefreshCw } from "lucide-react";
import "./RegisterSuccessPanel.css";

/**
 * Bestätigungsansicht nach erfolgreicher Registrierung (E-Mail-Confirmation steht aus).
 * Tritt an die Stelle der Login-/Register-Tabs und ersetzt den vorherigen unscheinbaren
 * Info-Banner. Großer visueller Beat, klare nächste Schritte, kein Form-Rauschen.
 *
 * @param {Object} props
 * @param {function(string, object=):string} props.t
 * @param {string} props.email
 * @param {function():void} props.onBackToLogin
 * @param {function():void} props.onUseDifferentEmail
 * @param {function():void} [props.onResendEmail]
 * @param {boolean} [props.resendBusy]
 * @param {string} [props.resendNotice]
 */
export function RegisterSuccessPanel({
  t,
  email,
  onBackToLogin,
  onUseDifferentEmail,
  onResendEmail,
  resendBusy = false,
  resendNotice = "",
}) {
  const tr = typeof t === "function" ? t : (k) => k;

  const mailtoHref = useMemo(() => {
    const e = String(email || "").trim().toLowerCase();
    if (!e) return null;
    const domain = e.split("@")[1] || "";
    if (!domain) return null;
    if (domain.includes("gmail.")) return "https://mail.google.com";
    if (domain.includes("outlook.") || domain.includes("hotmail.") || domain.includes("live.")) {
      return "https://outlook.live.com/mail/0/inbox";
    }
    if (domain.includes("yahoo.")) return "https://mail.yahoo.com";
    if (domain.includes("gmx.") || domain.includes("web.de") || domain.includes("t-online.")) {
      return `https://www.${domain}`;
    }
    return null;
  }, [email]);

  return (
    <section className="registerSuccess" aria-live="polite">
      <div className="registerSuccess__iconWrap" aria-hidden="true">
        <div className="registerSuccess__iconHalo" />
        <MailCheck size={36} strokeWidth={1.8} className="registerSuccess__icon" />
      </div>

      <h2 className="registerSuccess__title">{tr("auth.register.success.title")}</h2>
      <p className="registerSuccess__lead">
        {tr("auth.register.success.lead")}
      </p>

      {email ? (
        <div className="registerSuccess__emailRow" aria-label={tr("auth.register.success.sentToAria")}>
          <Mail size={16} strokeWidth={2} aria-hidden="true" />
          <span className="registerSuccess__emailValue">{email}</span>
        </div>
      ) : null}

      <ol className="registerSuccess__steps">
        <li>
          <span className="registerSuccess__stepNum">1</span>
          <span>{tr("auth.register.success.step1")}</span>
        </li>
        <li>
          <span className="registerSuccess__stepNum">2</span>
          <span>{tr("auth.register.success.step2")}</span>
        </li>
        <li>
          <span className="registerSuccess__stepNum">3</span>
          <span>{tr("auth.register.success.step3")}</span>
        </li>
      </ol>

      <div className="registerSuccess__actions">
        {mailtoHref ? (
          <a
            className="registerSuccess__primaryBtn"
            href={mailtoHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{tr("auth.register.success.openMailbox")}</span>
            <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
          </a>
        ) : null}
        <button type="button" className="registerSuccess__ghostBtn" onClick={onBackToLogin}>
          {tr("auth.register.success.backToLogin")}
        </button>
      </div>

      <div className="registerSuccess__secondary">
        {typeof onResendEmail === "function" ? (
          <button
            type="button"
            className="registerSuccess__textLink"
            disabled={resendBusy}
            onClick={onResendEmail}
          >
            <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
            <span>
              {resendBusy
                ? tr("auth.register.success.resending")
                : tr("auth.register.success.resend")}
            </span>
          </button>
        ) : null}
        <button type="button" className="registerSuccess__textLink" onClick={onUseDifferentEmail}>
          {tr("auth.register.success.useDifferentEmail")}
        </button>
      </div>

      {resendNotice ? (
        <p className="registerSuccess__notice" role="status">
          {resendNotice}
        </p>
      ) : null}

      <p className="registerSuccess__hint">
        {tr("auth.register.success.hint")}
      </p>
    </section>
  );
}
